import type { SiteLayer } from '../../types/site';

export type SiteLayerAuthority =
  | 'canonical-subcollection'
  | 'legacy-non-landgate-fallback'
  | 'empty';

export interface ResolvedSiteLayers {
  layers: SiteLayer[];
  authority: SiteLayerAuthority;
  requiresMigration: boolean;
}

/**
 * Resolve the layer set used by evidence/report paths during the legacy-mirror
 * migration window.
 *
 * A non-empty `/layers` subcollection is authoritative. When that collection
 * is empty, historic non-LandGate mirror rows are retained so a pre-migration
 * site's KMZ/ArcGIS infrastructure does not disappear from SEDs and reports.
 * Legacy LandGate rows are never accepted through this fallback: promotion
 * history is review-sensitive and must exist in the canonical collection.
 */
export function resolveCanonicalSiteLayers(
  canonicalLayers: readonly SiteLayer[],
  legacyMirror: readonly SiteLayer[] | null | undefined,
  canonicalVersion: unknown,
): ResolvedSiteLayers {
  if (canonicalLayers.length > 0) {
    return {
      layers: [...canonicalLayers],
      authority: 'canonical-subcollection',
      requiresMigration: false,
    };
  }

  // Once a canonical writer has stamped the marker, an empty subcollection is
  // an intentional deletion state. Falling back here would resurrect layers.
  if (canonicalVersion === 1) {
    return {
      layers: [],
      authority: 'canonical-subcollection',
      requiresMigration: false,
    };
  }

  const legacyNonLandGate = (legacyMirror ?? [])
    .filter(layer => layer?.source !== 'landgate-import');
  if (legacyNonLandGate.length > 0) {
    return {
      layers: legacyNonLandGate,
      authority: 'legacy-non-landgate-fallback',
      requiresMigration: true,
    };
  }

  return {
    layers: [],
    authority: 'empty',
    requiresMigration: false,
  };
}

export interface SiteLayerMirrorDescriptor {
  siteLayersCanonicalVersion?: unknown;
  siteLayersMirrorComplete?: unknown;
  siteLayerCount?: unknown;
  siteLayers?: readonly SiteLayer[] | null;
  generationImpactLayoutHead?: unknown;
  _siteLayersCanonicalComplete?: boolean;
  _siteLayersHydrationPending?: boolean;
  _siteLayersHydrationError?: string;
}

/**
 * Conservative completeness check for a parent-document `siteLayers` mirror.
 *
 * A parent mirror is provably complete without a `/layers` subcollection read
 * ONLY when `siteLayersCanonicalVersion === 1`, `siteLayersMirrorComplete === true`
 * (strict boolean `true` — never `undefined`, `null`, or `false`), no active
 * server-owned `generationImpactLayoutHead` mirror is present, and `siteLayerCount`
 * is a non-negative integer matching `siteLayers.length`.
 */
export function isVerifiedCompleteSiteLayerMirror(
  site: SiteLayerMirrorDescriptor | null | undefined,
): boolean {
  if (!site) return false;
  if (site.siteLayersCanonicalVersion !== 1) return false;
  if (site.siteLayersMirrorComplete !== true) return false;
  if (site.generationImpactLayoutHead != null) return false;
  if (!Array.isArray(site.siteLayers)) return false;
  if (
    typeof site.siteLayerCount !== 'number'
    || !Number.isInteger(site.siteLayerCount)
    || site.siteLayerCount < 0
  ) {
    return false;
  }
  return site.siteLayers.length === site.siteLayerCount;
}

/**
 * Per-site write-readiness predicate for full-array layer mutations (`updateSiteLayers`).
 *
 * Blocks layer writes while Phase 2 canonical hydration is pending, after a
 * canonical hydration error, or when the site carries an unhydrated partial or
 * unknown-completeness parent mirror.
 */
export function isSiteLayerSetWriteReady(
  site: SiteLayerMirrorDescriptor | null | undefined,
): boolean {
  if (!site) return true;
  if (site._siteLayersHydrationPending === true || Boolean(site._siteLayersHydrationError)) {
    return false;
  }
  if (site._siteLayersCanonicalComplete === false) {
    return false;
  }
  if (site._siteLayersCanonicalComplete === true) {
    return true;
  }
  const mirrorLen = Array.isArray(site.siteLayers) ? site.siteLayers.length : 0;
  if (typeof site.siteLayerCount === 'number' && mirrorLen < site.siteLayerCount) {
    return false;
  }
  if (site.siteLayersMirrorComplete === false) {
    return false;
  }
  if (
    site.siteLayersCanonicalVersion === 1
    && site.siteLayersMirrorComplete !== true
    && site.generationImpactLayoutHead == null
  ) {
    return false;
  }
  return true;
}
